/**
 * M7 S39 part 3: the Finale screen's controller ("Transit Priority"), on the Logs screen's pattern (`logsHost.tsx`).
 * It holds the `FinaleState` and, on the hand-in step, S34's `BundleState`; routes `NavInput` to whichever is on
 * screen; opens the bundle picker when the flow reaches `bundle`; and hands the picked bundle in through
 * `source.handIn(path, entries)`, then reports `handedIn` to the flow. Backing out of the picker reports a failed
 * hand-in (the flow returns to the offer, nothing committed).
 *
 * The Logs overlay mounts it (`logsHost.tsx`, opened from the case tab's finale row or the debug hook), so its keys
 * and the pad come through the Logs screen's routing. Node-tested; UI events only.
 */
import type { ReactNode } from 'react'
import type { Career } from '../../career/types'
import { logger } from '../../core/logger'
import type { CaseData } from '../../story/bundle'
import type { FinaleSource } from '../../story-link/finale'
import type { NavInput } from '../strategic/mount'
import { Finale } from './Finale'
import { buildBundleView, createBundleState, reduceBundle, type BundleAction, type BundleState, type BundleView } from './models/bundle'
import type { ClaimPanelConfig } from './models/claim'
import { buildFinaleView, createFinaleState, reduceFinale, type FinaleAction, type FinaleState, type FinaleView } from './models/finale'

/** What the Logs screen's story source carries for the finale (the story link provides it). */
export interface LogsFinaleSource {
  source: FinaleSource
  /** True while a path is on offer (the case tab then shows the finale row). */
  open(): boolean
  /** The bundle picker's data (`storyData()`). */
  caseData(): CaseData
}

export interface FinaleControllerDeps {
  finale: LogsFinaleSource
  career: () => Career | null
  /** The bundle picker's words (`claimPanel.json`). */
  bundleConfig: ClaimPanelConfig
  onClose: () => void
  /** After every action (the owner re-renders). */
  onChange?: () => void
}

export interface FinaleBundleOpen {
  view: BundleView
  state: BundleState
}

export interface FinaleController {
  readonly state: FinaleState
  readonly view: FinaleView
  /** The hand-in's picker while the step is `bundle`. */
  readonly bundle: FinaleBundleOpen | null
  dispatch(a: FinaleAction): void
  bundleDispatch(a: BundleAction): void
  /** Keyboard and pad: to the picker on the hand-in, else to the flow. */
  input(i: NavInput): void
  /** Re-reads the source (a story result landed). */
  refresh(): void
  /** The screen as a React node. */
  node(): ReactNode
}

export function createFinaleController(deps: FinaleControllerDeps): FinaleController {
  const src = deps.finale.source
  let state = createFinaleState()
  let view = buildFinaleView(state, src)
  let bundle: BundleState | null = null

  function bundleView(career: Career, s: BundleState): BundleView {
    return buildBundleView(career, s, deps.finale.caseData(), deps.bundleConfig)
  }

  function rebuild(): void {
    view = buildFinaleView(state, src)
    if (state.step.kind === 'bundle') bundle ??= createBundleState()
    else bundle = null
  }

  function dispatch(a: FinaleAction): void {
    let r: ReturnType<typeof reduceFinale>
    try {
      r = reduceFinale(state, a, src, view)
    } catch (err: unknown) {
      logger.warn('Finale screen: an action failed', { action: a.type, err })
      return
    }
    state = r.state
    rebuild()
    if (r.effect?.kind === 'close') deps.onClose()
    else deps.onChange?.()
  }

  function bundleDispatch(a: BundleAction): void {
    const career = deps.career()
    const step = state.step
    if (bundle === null || career === null || step.kind !== 'bundle') return
    const r = reduceBundle(career, bundle, a, bundleView(career, bundle), deps.finale.caseData(), deps.bundleConfig)
    bundle = r.state
    const e = r.effect
    if (e === null) {
      deps.onChange?.()
      return
    }
    if (e.kind === 'close') {
      dispatch({ type: 'handedIn', ok: false })
      return
    }
    let ok = false
    try {
      ok = src.handIn(step.path, e.entries)
    } catch (err: unknown) {
      logger.warn('Finale screen: could not hand the bundle in', { path: step.path, err })
    }
    dispatch({ type: 'handedIn', ok })
  }

  function openBundle(): FinaleBundleOpen | null {
    const career = deps.career()
    return bundle === null || career === null ? null : { view: bundleView(career, bundle), state: bundle }
  }

  return {
    get state(): FinaleState {
      return state
    },
    get view(): FinaleView {
      return view
    },
    get bundle(): FinaleBundleOpen | null {
      return openBundle()
    },
    dispatch,
    bundleDispatch,
    input(i: NavInput): void {
      if (bundle !== null) bundleDispatch({ type: 'nav', input: i })
      else dispatch({ type: 'nav', input: i })
    },
    refresh: rebuild,
    node(): ReactNode {
      const b = openBundle()
      const picker = b === null ? null : { ...b, config: deps.bundleConfig, dispatch: bundleDispatch }
      return <Finale view={view} state={state} dispatch={dispatch} bundle={picker} />
    },
  }
}
